"use client"

import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import {
  RRHH_ESTADOS_LIQUIDACION,
  extractErrorMessage,
  formatCurrency,
  formatDuracionMinutos,
  formatFecha,
  formatPeriodoMensual,
  getEmpleadoNombreCompleto,
  getEstadoLiquidacionMeta,
} from "./rrhhUi.js"

const getCurrentPeriod = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
}

const buildInitialForm = (liquidacion = null, mode = "create") => ({
  empleado: liquidacion?.entidad ? {
    idEntidad: liquidacion.idEntidad,
    entidad: liquidacion.entidad,
  } : null,
  periodo: liquidacion?.periodo || getCurrentPeriod(),
  estado: liquidacion?.estado || "BORRADOR",
  fechaPago: liquidacion?.fechaPago || "",
  observaciones: liquidacion?.observaciones || "",
})

const ModalRrhhLiquidacion = ({
  open,
  onClose,
  onSubmit,
  liquidacion = null,
  empleados = [],
  mode = "create",
  submitting = false,
}) => {
  const [form, setForm] = useState(buildInitialForm(liquidacion, mode))
  const [error, setError] = useState(null)

  const isCreate = mode === "create"
  const estadoMeta = useMemo(() => getEstadoLiquidacionMeta(form.estado), [form.estado])

  useEffect(() => {
    if (!open) return
    setForm(buildInitialForm(liquidacion, mode))
    setError(null)
  }, [open, liquidacion, mode])

  const detalleRows = liquidacion?.detalles || []
  const novedadesRows = liquidacion?.novedades || []

  const handleSubmit = async () => {
    if (isCreate) {
      if (!form.empleado?.idEntidad || !form.periodo) {
        setError("Empleado y período son requeridos.")
        return
      }

      setError(null)
      try {
        await onSubmit({
          idEntidad: form.empleado.idEntidad,
          periodo: form.periodo,
          observaciones: form.observaciones,
        })
      } catch (submitError) {
        setError(extractErrorMessage(submitError, "No se pudo generar la liquidación."))
      }
      return
    }

    if (!form.estado) {
      setError("El estado es requerido.")
      return
    }

    setError(null)
    try {
      await onSubmit({
        estado: form.estado,
        fechaPago: form.estado === "PAGADA" ? form.fechaPago || undefined : undefined,
        observaciones: form.observaciones,
      })
    } catch (submitError) {
      setError(extractErrorMessage(submitError, "No se pudo actualizar la liquidación."))
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{isCreate ? "Generar liquidación" : "Detalle de liquidación"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Autocomplete
              fullWidth
              options={empleados}
              value={form.empleado}
              disabled={!isCreate}
              isOptionEqualToValue={(option, value) => option.idEntidad === value.idEntidad}
              getOptionLabel={(option) => {
                const nombre = getEmpleadoNombreCompleto(option)
                const legajo = option?.rrhhEmpleado?.legajo || option?.entidad?.rrhhEmpleado?.legajo
                return legajo ? `${nombre} (${legajo})` : nombre
              }}
              onChange={(_, value) =>
                setForm((prev) => ({
                  ...prev,
                  empleado: value,
                }))
              }
              renderInput={(params) => <TextField {...params} label="Empleado" required />}
            />

            <TextField
              label="Período"
              type="month"
              value={form.periodo}
              disabled={!isCreate}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  periodo: event.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              required
              sx={{ minWidth: { md: 180 } }}
            />
          </Stack>

          {!isCreate && (
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
              <Autocomplete
                sx={{ minWidth: { md: 220 } }}
                options={RRHH_ESTADOS_LIQUIDACION}
                value={RRHH_ESTADOS_LIQUIDACION.find((item) => item.value === form.estado) || null}
                onChange={(_, value) =>
                  setForm((prev) => ({
                    ...prev,
                    estado: value?.value || "BORRADOR",
                  }))
                }
                renderInput={(params) => <TextField {...params} label="Estado" required />}
              />

              {form.estado === "PAGADA" && (
                <TextField
                  label="Fecha de pago"
                  type="date"
                  value={form.fechaPago}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      fechaPago: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { md: 180 } }}
                />
              )}

              <Chip
                label={estadoMeta.label}
                sx={{
                  alignSelf: { xs: "flex-start", md: "center" },
                  backgroundColor: estadoMeta.backgroundColor,
                  color: estadoMeta.color,
                  fontWeight: 700,
                }}
              />
            </Stack>
          )}

          <TextField
            label="Observaciones"
            value={form.observaciones}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                observaciones: event.target.value,
              }))
            }
            multiline
            minRows={3}
          />

          {!isCreate && liquidacion && (
            <>
              <Divider />

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap">
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Período</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatPeriodoMensual(liquidacion.periodo)}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Haberes</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(liquidacion.totalHaberes)}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Descuentos</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(liquidacion.totalDescuentos)}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Neto</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(liquidacion.totalNeto)}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Horas extra</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatDuracionMinutos(liquidacion.totalMinutosExtra)}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 150 }}>
                  <Typography variant="caption" color="text.secondary">Liquidada</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatFecha(liquidacion.fechaLiquidacion)}</Typography>
                </Paper>
              </Stack>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
                  Detalle de conceptos
                </Typography>
                <Stack spacing={1}>
                  {detalleRows.map((detalle) => (
                    <Paper
                      key={detalle.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: detalle.tipoConcepto === "DESCUENTO" ? "rgba(239, 68, 68, 0.05)" : "rgba(15, 23, 42, 0.02)",
                      }}
                    >
                      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {detalle.descripcion}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {detalle.tipoConcepto} · {detalle.origen} · Base {formatCurrency(detalle.baseCalculo)} · Cantidad {detalle.cantidad ?? "-"}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatCurrency(detalle.importe)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
                  Novedades del período
                </Typography>
                <Stack spacing={1}>
                  {novedadesRows.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No hay novedades registradas para este período.
                    </Typography>
                  )}
                  {novedadesRows.map((novedad) => (
                    <Paper key={novedad.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {novedad.descripcion}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {novedad.categoria} · {novedad.tipoConcepto} · {formatFecha(novedad.fechaNovedad)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatCurrency(novedad.montoTotal)}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? "Guardando..." : isCreate ? "Generar liquidación" : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModalRrhhLiquidacion
